import { useState, useEffect } from 'react';
import { CreditCard, Check, AlertCircle, AlertTriangle, Plus, Clock, X, Repeat, TrendingDown, Mail, RotateCcw } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import NavigationHeader from '../components/NavigationHeader';
import { config } from '@safermark/shared/config';
import { 
  getAllRecurringPlans, 
  getAllPrepaidPlans, 
  formatPrice, 
  getRecurringPlan,
  type RecurringPlanId, 
  type PrepaidPlanId,
  getDisplayPaymentMethods,
  PAYMENT_CONFIG
} from '@safermark/shared';

export default function SubscriptionPage() {
  const { token, user, refreshUser, refreshUserAfterPayment, refreshPremiumStatus, isPremium } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [paymentMode, setPaymentMode] = useState<'recurring' | 'prepaid'>('recurring');
  // const monthlyPlan = getRecurringPlan('monthly');
  // const yearlyPlan = getRecurringPlan('yearly');

  // Refresh user data on page entry to ensure subscription status is current
  useEffect(() => {
    if (token) {
      refreshUser(false).catch(error => {
        console.warn('Failed to refresh user data on subscription page entry:', error);
      });
    }
  }, [token, refreshUser]);

  // Auto-refresh premium status if user appears non-premium on subscription page entry
  // This is particularly important since users often arrive here expecting premium status
  useEffect(() => {
    if (token && user && !isPremium) {
      console.log('SubscriptionPage: User appears non-premium, refreshing status to ensure it\'s up-to-date');
      refreshPremiumStatus().catch(error => {
        console.warn('Failed to refresh premium status on subscription page entry:', error);
      });
    }
  }, [token, user, isPremium, refreshPremiumStatus]);

  // Helper functions
  const formatRemainingTime = (endTimestamp: number) => {
    const now = Date.now();
    const endTime = endTimestamp * 1000; // Convert to milliseconds
    const timeLeft = endTime - now;
    
    if (timeLeft <= 0) {
      return 'Expired';
    }
    
    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    
    if (days > 0) {
      return `${days} day${days !== 1 ? 's' : ''}`;
    } else {
      // For less than a day, show "1 day" to avoid confusion
      return '1 day';
    }
  };

  // Get the current subscription plan directly from stored user data
  const getCurrentSubscriptionPlan = () => {
    if (user?.membershipType !== 'premium_subscribed' || !user?.subscriptionPlanId) {
      return null;
    }
    
    return getRecurringPlan(user.subscriptionPlanId);
  };

  const currentSubscriptionPlan = getCurrentSubscriptionPlan();

  const getSubscriptionDisplayStatus = () => {
    if (user?.membershipType === 'premium_prepaid') {
      return 'Premium (Prepaid)';
    } else if (user?.membershipType === 'premium_subscribed') {
      if (user.subscriptionCancelAtPeriodEnd) {
        return 'Premium (Cancelling)';
      }
      const planType = currentSubscriptionPlan?.id === 'yearly' ? 'Yearly' : 'Monthly';
      return `Premium (${planType} Subscription)`;
    }
    return 'Free';
  };

  const getSubscriptionDescription = () => {
    if (user?.membershipType === 'premium_prepaid' && user.prepaidUntil) {
      const remaining = formatRemainingTime(user.prepaidUntil);
      return `Premium features active until ${new Date(user.prepaidUntil * 1000).toLocaleDateString()}. ${remaining} remaining.`;
    } else if (user?.membershipType === 'premium_subscribed') {
      if (user.subscriptionCancelAtPeriodEnd && user.subscriptionCurrentPeriodEnd) {
        const remaining = formatRemainingTime(user.subscriptionCurrentPeriodEnd);
        return `Premium features active. Cancels in ${remaining}`;
      }
      
      // Show renewal information if available
      const renewalDate = user.subscriptionNextRenewal || user.subscriptionCurrentPeriodEnd;
      if (renewalDate) {
        const renewalDateStr = new Date(renewalDate * 1000).toLocaleDateString();
        const remaining = formatRemainingTime(renewalDate);
        return `Cloud sync, web access, and premium features. Renews on ${renewalDateStr} (in ${remaining}).`;
      }
      
      return 'Cloud sync, web access, and premium features';
    }
    return 'Chrome extension with local-only storage';
  };

  const isActiveSubscription = user?.membershipType === 'premium_subscribed';
  const isPrepaidUser = user?.membershipType === 'premium_prepaid';
  const isPendingCancellation = user?.subscriptionCancelAtPeriodEnd === true;
  const canSubscribe = user?.membershipType === 'free'; // Can subscribe if free
  const canAddPrepaidTime = isPrepaidUser; // Prepaid users can always add more time

  // Check if user needs email verification for subscriptions
  const requiresEmailForSubscription = !user?.email || !user?.emailVerified;

  // Get pricing data from centralized config
  const recurringPlans = getAllRecurringPlans();
  const prepaidPlans = getAllPrepaidPlans();

  // Set payment mode to prepaid for existing prepaid users or users without email
  useEffect(() => {
    if (isPrepaidUser) {
      setPaymentMode('prepaid');
    } else if (requiresEmailForSubscription && canSubscribe) {
      // Default to prepaid for users without verified email addresses
      setPaymentMode('prepaid');
    }
  }, [isPrepaidUser, requiresEmailForSubscription, canSubscribe]);

  const handlePrepaidSubscribe = async (duration: PrepaidPlanId) => {
    if (!token) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${config.apiUrl}/payments/create-prepaid-checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          duration,
          successUrl: `${window.location.origin}/subscription?success=true`,
          cancelUrl: `${window.location.origin}/subscription?canceled=true`,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create checkout session');
      }

      const data = await response.json();
      window.location.href = data.url;
    } catch (err) {
      if (err instanceof TypeError && err.message.includes('fetch')) {
        setError('Cannot connect to API server. Please ensure the backend is running.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to start checkout');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (plan: RecurringPlanId) => {
    if (!token) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${config.apiUrl}/payments/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceType: plan,
          successUrl: `${window.location.origin}/subscription?success=true`,
          cancelUrl: `${window.location.origin}/subscription?canceled=true`,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create checkout session');
      }

      const data = await response.json();
      window.location.href = data.url;
    } catch (err) {
      if (err instanceof TypeError && err.message.includes('fetch')) {
        setError('Cannot connect to API server. Please ensure the backend is running.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to start checkout');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEndSubscriptionImmediately = async () => {
    if (!token || !confirm('Are you sure you want to end your subscription immediately? You will lose access to premium features right away.')) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${config.apiUrl}/payments/end-subscription-immediately`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to end subscription');
      }

      setSuccess('Subscription ended immediately. You can now subscribe again if you wish.');
      
      // User data will be refreshed on next page load
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to end subscription');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!token || !confirm('Are you sure you want to cancel your subscription?')) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${config.apiUrl}/payments/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to cancel subscription');
      }

      setSuccess('Subscription canceled successfully. You will continue to have access until the end of your billing period.');
      
      // User data will be refreshed on next page load
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel subscription');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check for success/cancel parameters
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      setSuccess('Subscription activated successfully! You now have access to premium features.');
      
      // Refresh user data to update subscription status immediately
      // Use retry logic since webhook might take a moment to process
      const refreshWithRetry = async (retries = 5, delay = 3000) => {
        console.log('Starting subscription refresh with retry logic...');
        for (let i = 0; i < retries; i++) {
          try {
            console.log(`Refresh attempt ${i + 1}/${retries}`);
            const updatedUser = await refreshUserAfterPayment();
            console.log('User data refreshed successfully - checking premium status...');
            
            // Check the fresh user data
            const freshIsPremium = updatedUser?.isPremium || false;
            console.log('Fresh user premium status:', freshIsPremium ? 'Premium' : 'Free');
            console.log('User membership type:', updatedUser?.membershipType);
            
            if (freshIsPremium) {
              console.log('Premium status detected - UI should update now');
              break;
            } else {
              console.log('Still showing Free - webhook may not have processed yet');
              if (i < retries - 1) {
                console.log(`Waiting ${delay}ms before retry...`);
                await new Promise(resolve => setTimeout(resolve, delay));
              }
            }
          } catch (refreshError) {
            console.warn(`Refresh attempt ${i + 1} failed:`, refreshError);
            if (i < retries - 1) {
              console.log(`Waiting ${delay}ms before retry...`);
              await new Promise(resolve => setTimeout(resolve, delay));
            }
          }
        }
      };
      
      refreshWithRetry().catch((finalError) => {
        console.warn('All refresh attempts failed after subscription success:', finalError);
        // Don't show error to user - subscription was successful
      });
      
      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (urlParams.get('canceled') === 'true') {
      setError('Checkout was canceled. You can try again at any time.');
      
      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationHeader />

      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Subscription</h2>
          <p className="text-gray-600">
            Manage your subscription and billing settings
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
            <div className="flex">
              <Check className="h-5 w-5 text-green-400 mr-2" />
              <p className="text-sm text-green-600">{success}</p>
            </div>
          </div>
        )}

        {/* Current Plan */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Plan</h3>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {getSubscriptionDisplayStatus()}
              </div>
              <div className="text-gray-600">
                {getSubscriptionDescription()}
              </div>
              {isPendingCancellation && user?.subscriptionCurrentPeriodEnd && (
                <div className="mt-2 text-sm text-orange-600 font-medium">
                  <AlertTriangle className="h-4 w-4 inline mr-2" />
                  Subscription ends on {new Date(user.subscriptionCurrentPeriodEnd * 1000).toLocaleDateString()}
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">
                {isPrepaidUser ? 'Prepaid' : (isActiveSubscription && currentSubscriptionPlan ? `${formatPrice(currentSubscriptionPlan.price)}${currentSubscriptionPlan.period}` : '')}
              </div>
              {!isPremium && !isPrepaidUser && (
                <div className="text-lg text-gray-600 mt-1">
                  No active subscription
                </div>
              )}
              <div className="mt-2 space-y-2">
                {isActiveSubscription && !isPendingCancellation && (
                  <button
                    onClick={handleCancelSubscription}
                    disabled={loading}
                    className="block text-red-600 hover:text-red-700 text-sm font-medium disabled:opacity-50"
                  >
                    Cancel Subscription
                  </button>
                )}
                {isPrepaidUser && (
                  <button
                    onClick={() => setPaymentMode('prepaid')}
                    disabled={loading}
                    className="block text-blue-600 hover:text-blue-700 text-sm font-medium disabled:opacity-50"
                  >
                    Add More Time
                  </button>
                )}
                {isPendingCancellation && (
                  <button
                    onClick={handleEndSubscriptionImmediately}
                    disabled={loading}
                    className="block text-red-600 hover:text-red-700 text-sm font-medium disabled:opacity-50"
                  >
                    End Immediately
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Payment Mode Toggle */}
        {(canSubscribe || canAddPrepaidTime) && (
          <>
            {/* Payment Mode Toggle */}
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {canAddPrepaidTime ? 'Add More Premium Time' : 'Choose Payment Method'}
              </h3>
              {canAddPrepaidTime ? (
                <div className="text-sm text-gray-600 mb-4">
                  <p><Plus className="h-3 w-3 inline mr-2" />Extend your current premium access with additional prepaid time</p>
                  <p><Clock className="h-3 w-3 inline mr-2" />New time will be added to your existing expiration date</p>
                  <p><CreditCard className="h-3 w-3 inline mr-2" />No subscription required - pay once and enjoy premium features</p>
                </div>
              ) : (
                <>
                  <div className="flex bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setPaymentMode('recurring')}
                      className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                        paymentMode === 'recurring'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Recurring Subscription
                    </button>
                    <button
                      onClick={() => setPaymentMode('prepaid')}
                      className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                        paymentMode === 'prepaid'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Prepaid (One-time Payment)
                    </button>
                  </div>
                </>
              )}
              {!canAddPrepaidTime && (
              <div className="mt-4 text-sm text-gray-600">
                {paymentMode === 'recurring' ? (
                  <>
                    <p><Repeat className="h-3 w-3 inline mr-2" />Monthly or yearly billing with automatic renewal</p>
                    <p><X className="h-3 w-3 inline mr-2" />Cancel anytime, premium access continues until period ends</p>
                    {requiresEmailForSubscription && (
                      <p className="text-orange-600 font-medium mt-2">
                        <AlertTriangle className="h-4 w-4 inline mr-2" />
                        Requires verified email address for account management
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <p><CreditCard className="h-3 w-3 inline mr-2" />One-time payment, no automatic renewal</p>
                    <p><TrendingDown className="h-3 w-3 inline mr-2" />Longer durations offer better savings</p>
                    <p><Mail className="h-3 w-3 inline mr-2" />No email verification required</p>
                  </>
                )}
              </div>
              )}
            </div>

            {/* Recurring Plans */}
            {paymentMode === 'recurring' && !canAddPrepaidTime && (
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                {recurringPlans.map((plan) => (
                  <div
                    key={plan.id}
                    className={`bg-white p-8 rounded-xl shadow-lg border-2 ${
                      plan.recommended ? 'border-blue-600' : 'border-gray-200'
                    } relative`}
                  >
                    {plan.recommended && (
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                        <span className="bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                          Best Value
                        </span>
                      </div>
                    )}
                    <div className="text-center">
                      <h4 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h4>
                      <div className="text-4xl font-bold text-blue-600 mb-2">
                        {formatPrice(plan.price)}<span className="text-lg text-gray-600">{plan.period}</span>
                      </div>
                      {plan.savings && (
                        <div className="text-sm text-green-600 font-medium mb-6">
                          {plan.savings}
                        </div>
                      )}
                      <ul className="text-left space-y-3 mb-8">
                        {plan.description.map((feature, index) => (
                          <li key={index} className="flex items-center">
                            <Check className="h-4 w-4 text-green-500 mr-3" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      <button
                        onClick={() => handleSubscribe(plan.id)}
                        disabled={loading || requiresEmailForSubscription}
                        className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Processing...' : `Subscribe ${plan.name}`}
                      </button>
                      {requiresEmailForSubscription && (
                        <p className="text-xs text-orange-600 mt-2">
                          Add and verify email in profile to subscribe
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Prepaid Plans */}
            {(paymentMode === 'prepaid' || canAddPrepaidTime) && (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {prepaidPlans.map((plan) => (
                  <div
                    key={plan.id}
                    className={`bg-white p-6 rounded-xl shadow-lg border-2 ${
                      plan.recommended ? 'border-green-600' : 'border-gray-200'
                    } relative`}
                  >
                    {plan.recommended && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="bg-green-600 text-white px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap">
                          Most Popular
                        </span>
                      </div>
                    )}
                    <div className="text-center">
                      <h4 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h4>
                      <div className="text-3xl font-bold text-blue-600 mb-2">
                        {formatPrice(plan.price)}
                      </div>
                      {plan.savings > 0 && (
                        <div className="text-sm text-green-600 font-medium mb-4">
                          Save {formatPrice(plan.savings)}!
                        </div>
                      )}
                      <p className="text-sm text-gray-600 mb-6">
                        {plan.description}
                      </p>
                      <button
                        onClick={() => handlePrepaidSubscribe(plan.id)}
                        disabled={loading}
                        className="w-full bg-blue-600 text-white py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Processing...' : `Pay ${formatPrice(plan.price)}`}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Premium Status Notice */}
        {!canSubscribe && !isPendingCancellation && !canAddPrepaidTime && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <Check className="h-6 w-6 text-blue-600 mt-1" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">
                  You're All Set!
                </h3>
                <p className="text-blue-800">
                  You already have an active premium subscription. Enjoy all the premium features!
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Pending Cancellation Notice */}
        {isPendingCancellation && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 mb-8">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <AlertCircle className="h-6 w-6 text-orange-600 mt-1" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-semibold text-orange-900 mb-2">
                  Subscription Scheduled for Cancellation
                </h3>
                <p className="text-orange-800 mb-4">
                  Your subscription is scheduled to cancel at the end of the current billing period. 
                  You'll continue to have premium access until then.
                </p>
                <div className="space-y-2">
                  <p className="text-sm text-orange-700">
                    <RotateCcw className="h-3 w-3 inline mr-2" />To resubscribe, you'll need to end your current subscription immediately
                  </p>
                  <p className="text-sm text-orange-700">
                    <Clock className="h-3 w-3 inline mr-2" />Or wait until your current period ends to subscribe again
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payment Methods */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Methods</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {getDisplayPaymentMethods().map((method) => (
              <div key={method.id} className={`flex items-center p-3 rounded-lg border-2 ${
                method.enabled 
                  ? 'border-green-200 bg-green-50' 
                  : 'border-gray-200 bg-gray-50'
              }`}>
                <div className="flex items-center flex-1">
                  {method.icon === 'CreditCard' ? (
                    <CreditCard className={`h-6 w-6 mr-3 ${method.color}`} />
                  ) : (
                    <span className={`font-bold mr-3 text-xl ${method.color}`}>
                      {method.icon}
                    </span>
                  )}
                  <div className="flex-1">
                    <span className={`font-medium ${
                      method.enabled ? 'text-gray-900' : 'text-gray-500'
                    }`}>
                      {method.name}
                    </span>
                    {method.comingSoon && (
                      <span className="ml-2 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                        Coming Soon
                      </span>
                    )}
                  </div>
                </div>
                {method.enabled && (
                  <Check className="h-5 w-5 text-green-600 ml-2" />
                )}
              </div>
            ))}
          </div>
          <p className="text-gray-500 text-sm">
            {PAYMENT_CONFIG.processorNote}
          </p>
        </div>
      </div>
    </div>
  );
}